import React, { useState, useEffect, useMemo, useRef } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Btn, Input, Modal, Sel } from "./ui";
import { supabase, isSupabaseReady } from "./supabase";
import { criarPedidoWix } from "./PlantaoModais";
import { calcAwardVitalicio } from "./awards";
import { AWARDS } from "./data";

// ═══════════════════════════════════════════════════════════════════════
// MÓDULO FINANCEIRO — Ciclos mensais de pagamento
// 1 NF por corretor por mês de competência (mes_ref = 'YYYY-MM').
// Pagamento previsto: primeiro dia útil ≥ 10 do mês seguinte.
// Admin: gestão completa | Corretor: visualização + upload NF
// ═══════════════════════════════════════════════════════════════════════

const STATUS_LABELS = {
  aberto:               { label: "Mês em aberto",  cor: T.info, icon: "🟢" },
  fechado:              { label: "Fechado",        cor: T.warn, icon: "🔒" },
  nf_pendente:          { label: "NF pendente",    cor: T.warn, icon: "📄" },
  nf_enviada:           { label: "NF enviada",     cor: T.info, icon: "📤" },
  nf_aprovada:          { label: "NF aprovada",    cor: T.ok,   icon: "✅" },
  aguardando_pagamento: { label: "Aguardando pgto",cor: T.gold, icon: "⏳" },
  pago:                 { label: "Pago",           cor: T.ok,   icon: "💰" },
};

function mesAtualRef() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMes(mesRef) {
  if (!mesRef) return "—";
  const [y, m] = mesRef.split("-").map(Number);
  const nomes = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  return `${nomes[m - 1]} ${y}`;
}

function fmtData(d) {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleDateString("pt-BR");
}

// Data prevista de pagamento de um ciclo: primeiro dia útil ≥ 10 do mês SEGUINTE ao mes_ref.
function dataPrevistaPagamento(mesRef) {
  if (!mesRef) return null;
  const [y, m] = mesRef.split("-").map(Number); // m = 1..12
  let ny = y, nm = m + 1;
  if (nm > 12) { nm = 1; ny = y + 1; }
  const d = new Date(ny, nm - 1, 10);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1); // pula fim de semana
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Status efetivo: combina o gravado no banco com regra "mês passado vira nf_pendente"
function statusEfetivo(ciclo) {
  if (!ciclo) return "aberto";
  const atual = mesAtualRef();
  if (ciclo.status === "aberto" && ciclo.mes_ref < atual) return "nf_pendente";
  return ciclo.status;
}

export default function Financeiro({ corretores, currentUser }) {
  const [tab, setTab] = useState("resumo");
  const [ciclos, setCiclos] = useState([]);
  const [comissoes, setComissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtros
  const [fCorretor, setFCorretor] = useState("todos");
  const [fStatus, setFStatus] = useState("todos");
  const [fMes, setFMes] = useState("todos");
  const [fBusca, setFBusca] = useState("");
  // Filtros da aba Comissões
  const [fComCorretor, setFComCorretor] = useState("todos");
  const [fComTipo, setFComTipo] = useState("todos"); // todos | direta | residual | consorcio
  const [fComPeriodo, setFComPeriodo] = useState("todos"); // todos | mes | ano
  const [fComBusca, setFComBusca] = useState("");

  // upload NF modal
  const [uploadCiclo, setUploadCiclo] = useState(null);
  const [nfNumero, setNfNumero] = useState("");
  const [nfFile, setNfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileInputRef = useRef(null);

  const isAdmin = currentUser.role === "admin" || currentUser.role === "admin_master" || currentUser.role === "proprietario" || currentUser.role === "comercial";
  const isMaster = currentUser.role === "admin_master" || currentUser.role === "proprietario";
  const meuUuid = currentUser._corretorUuid;

  // Editar valor da comissão (só master/proprietário)
  async function editarValorComissao(c) {
    const novo = window.prompt(`Novo valor da comissão (atual: ${fmt.brl(parseFloat(c.comissao_valor ?? c.valor ?? 0))}):`, c.comissao_valor ?? c.valor ?? "");
    if (novo == null) return;
    const v = parseFloat(String(novo).replace(",", "."));
    if (isNaN(v) || v < 0) { alert("Valor inválido"); return; }
    const motivo = window.prompt("Motivo da alteração (será registrado):");
    try {
      // Recalcula % baseado no novo valor sobre o líquido (direta) ou comissão direta (residual)
      const base = parseFloat(c.valor_comissionavel ?? c.vendas?.valor_liquido ?? c.valor_venda ?? 0);
      const novoPct = base > 0 ? +((v / base) * 100).toFixed(2) : 0;
      const { error } = await supabase.from("comissoes").update({
        comissao_valor: v,
        saldo_aberto: v,
        valor: v,
        comissao_pct: novoPct,
        percentual: novoPct,
        observacoes: `[edit master] ${motivo || "sem motivo"} · ${new Date().toISOString()}`,
      }).eq("id", c.id);
      if (error) throw error;
      setComissoes(prev => prev.map(x => x.id === c.id
        ? { ...x, comissao_valor: v, saldo_aberto: v, valor: v, comissao_pct: novoPct, percentual: novoPct }
        : x));
    } catch (e) { alert("Erro: " + e.message); }
  }

  // % da comissão direta pela premiação (award) vitalícia do corretor
  function pctAwardCorretor(cor) {
    if (!cor) return null;
    const awardId = calcAwardVitalicio(cor).id;
    const award = AWARDS.find(a => a.id === awardId);
    return award?.pctVendaDireta ?? null;
  }

  // Recalcula UMA comissão direta pela premiação atual do corretor (auto, ao lado do editar)
  async function recalcularComissaoAward(c) {
    if ((c.nivel ?? 0) !== 0) { alert("Recálculo automático vale só pra venda direta (nível 0)."); return; }
    const cor = corretorMap[c.corretor_id];
    if (!cor) { alert("Corretor da comissão não encontrado."); return; }
    const pct = pctAwardCorretor(cor);
    if (pct == null) { alert("Premiação do corretor não encontrada."); return; }
    const base = parseFloat(c.valor_comissionavel ?? c.vendas?.valor_liquido ?? c.valor_venda ?? 0);
    const novoValor = +(base * pct / 100).toFixed(2);
    try {
      const { error } = await supabase.from("comissoes").update({
        comissao_valor: novoValor, saldo_aberto: novoValor, valor: novoValor,
        comissao_pct: pct, percentual: pct,
        observacoes: `[recalc award ${pct}%] ${new Date().toISOString()}`,
      }).eq("id", c.id);
      if (error) throw error;
      setComissoes(prev => prev.map(x => x.id === c.id
        ? { ...x, comissao_valor: novoValor, saldo_aberto: novoValor, valor: novoValor, comissao_pct: pct, percentual: pct }
        : x));
    } catch (e) { alert("Erro: " + e.message); }
  }

  // TEMPORÁRIO: recalcula em lote todas as comissões diretas pela premiação atual
  // (pra corrigir valores antigos que ficaram com % de nível anterior). Some depois.
  async function recalcularTodasAward() {
    const alvos = comissoes.filter(c => (c.nivel ?? 0) === 0 && !(c.vendas?.tipo_venda === "consorcio" && !c.vendas?.comissao_quitada));
    if (!alvos.length) { alert("Nenhuma comissão direta pra recalcular."); return; }
    if (!window.confirm(`Recalcular ${alvos.length} comissões diretas pela premiação atual de cada corretor?\n\n(Ação temporária pra corrigir valores antigos.)`)) return;
    let ok = 0, fail = 0;
    const updates = [];
    for (const c of alvos) {
      const cor = corretorMap[c.corretor_id];
      const pct = pctAwardCorretor(cor);
      if (pct == null) { fail++; continue; }
      const base = parseFloat(c.valor_comissionavel ?? c.vendas?.valor_liquido ?? c.valor_venda ?? 0);
      const novoValor = +(base * pct / 100).toFixed(2);
      const { error } = await supabase.from("comissoes").update({
        comissao_valor: novoValor, saldo_aberto: novoValor, valor: novoValor, comissao_pct: pct, percentual: pct,
        observacoes: `[recalc award lote ${pct}%] ${new Date().toISOString()}`,
      }).eq("id", c.id);
      if (error) { fail++; } else { ok++; updates.push({ id: c.id, novoValor, pct }); }
    }
    setComissoes(prev => prev.map(x => {
      const u = updates.find(uu => uu.id === x.id);
      return u ? { ...x, comissao_valor: u.novoValor, saldo_aberto: u.novoValor, valor: u.novoValor, comissao_pct: u.pct, percentual: u.pct } : x;
    }));
    alert(`Recálculo concluído: ${ok} atualizadas, ${fail} sem premiação/erro.`);
  }

  // Confirma quitação do consórcio → libera a comissão agendada pro pagamento
  async function marcarQuitado(c) {
    const vendaId = c.vendas?.id;
    if (!vendaId || !isSupabaseReady) return;
    if (!window.confirm("Confirmar quitação do consórcio? Vai gerar o pedido no Wix (número) e liberar a comissão pra pagamento.")) return;
    try {
      // Se a venda veio de um fechamento de plantão, pega a ficha (móveis) pra montar o pedido Wix
      const { data: leadsLink } = await supabase
        .from("plantao_leads")
        .select("id, moveis, pedido_info, financeiro, nome_lead, telefone_lead")
        .eq("venda_id", vendaId).limit(1);
      const leadLink = leadsLink && leadsLink[0];
      const ficha = leadLink
        ? { moveis: leadLink.moveis, pedido_info: leadLink.pedido_info, financeiro: leadLink.financeiro }
        : { moveis: [], pedido_info: { nome: c.vendas?.cliente_nome, telefone: c.vendas?.cliente_telefone }, financeiro: { valor_total: c.vendas?.valor } };
      const lead = leadLink || { id: vendaId, nome_lead: c.vendas?.cliente_nome, telefone_lead: c.vendas?.cliente_telefone };

      // vendaId → o endpoint atualiza ESSA venda (adiciona nº Wix + comissao_quitada=true)
      const w = await criarPedidoWix(lead, ficha, c.corretor_id, vendaId);
      if (w && w.ok && w.numero) {
        if (leadLink) {
          await supabase.from("plantao_leads").update({ wix_numero: String(w.numero), consorcio_quitado: true }).eq("id", leadLink.id);
        }
        setComissoes(prev => prev.map(x =>
          x.vendas?.id === vendaId
            ? { ...x, vendas: { ...x.vendas, comissao_quitada: true, wix_order_id: String(w.numero) } }
            : x
        ));
      } else {
        alert("Falha ao gerar pedido Wix: " + (w && (w.erro || JSON.stringify(w.resposta)) || "erro"));
      }
    } catch (e) { alert("Erro: " + e.message); }
  }

  // ─── carga ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      if (!isSupabaseReady) { setLoading(false); return; }
      try {
        let qc = supabase.from("ciclos_pagamento").select("*").order("mes_ref", { ascending: false });
        if (!isAdmin && meuUuid) qc = qc.eq("corretor_id", meuUuid);

        let qm = supabase.from("comissoes")
          .select("*, vendas(id, corretor_id, cliente_nome, valor, frete_valor, frete, taxa_cartao, taxa_cartao_comprovante, taxa_cartao_comprovantes, desconto_concedido, valor_liquido, tipo_venda, comissao_data_prevista, comissao_quitada)")
          .order("criado_em", { ascending: false })
          .limit(1000);
        if (!isAdmin && meuUuid) qm = qm.eq("corretor_id", meuUuid);

        // Roda as duas em paralelo (antes era em sequência = dobro do tempo)
        const [{ data: cs }, { data: com }] = await Promise.all([qc, qm]);
        setCiclos(cs || []);
        setComissoes(com || []);
      } catch (e) { console.error("[Financeiro]", e); }
      setLoading(false);
    }
    load();
  }, [isAdmin, meuUuid]);

  const corretorMap = useMemo(() => {
    const m = {};
    corretores.forEach(c => { if (c._uuid) m[c._uuid] = c; if (c.id) m[c.id] = c; });
    return m;
  }, [corretores]);

  // ─── derivações ────────────────────────────────────────────────────
  const mesAtual = mesAtualRef();
  const ciclosComStatus = useMemo(() =>
    ciclos.map(c => ({ ...c, _status: statusEfetivo(c) }))
  , [ciclos]);

  const cicloAberto = ciclosComStatus.find(c => c.mes_ref === mesAtual);
  const ciclosFechados = ciclosComStatus.filter(c => c.mes_ref !== mesAtual);

  // Meses disponíveis pra filtro
  const mesesDisponiveis = useMemo(() => {
    const s = new Set(ciclos.map(c => c.mes_ref).filter(Boolean));
    return ["todos", ...Array.from(s).sort().reverse()];
  }, [ciclos]);

  // Filtragem de ciclos
  const ciclosFiltrados = useMemo(() => {
    let r = ciclosComStatus;
    if (isAdmin && fCorretor !== "todos") r = r.filter(c => c.corretor_id === fCorretor);
    if (fStatus !== "todos") r = r.filter(c => c._status === fStatus);
    if (fMes !== "todos")    r = r.filter(c => c.mes_ref === fMes);
    if (fBusca) {
      const q = fBusca.toLowerCase();
      r = r.filter(c => {
        const cor = corretorMap[c.corretor_id];
        return (cor?.nome || "").toLowerCase().includes(q)
          || (c.nf_numero || "").toString().includes(q);
      });
    }
    return r;
  }, [ciclosComStatus, isAdmin, fCorretor, fStatus, fMes, fBusca, corretorMap]);

  // ─── Bônus Targets (calculados das comissões) ─────────────────────
  const agora = new Date();
  const umAnoAtras = new Date(agora); umAnoAtras.setFullYear(agora.getFullYear() - 1);
  const noventaDiasAtras = new Date(agora); noventaDiasAtras.setDate(agora.getDate() - 90);
  const inicioAno = new Date(agora.getFullYear(), 0, 1);

  // Comissão de rede (nivel > 0) último ano
  const comissaoRedeAno = comissoes
    .filter(c => (c.nivel ?? 0) > 0)
    .filter(c => new Date(c.criado_em) >= umAnoAtras)
    .reduce((s, c) => s + parseFloat(c.comissao_valor ?? c.valor ?? 0), 0);

  // Comissões de consórcio ainda não quitadas ficam AGENDADAS (fora do "a pagar")
  // Regime de competência (registrada/visível) + regime de caixa (paga só na quitação)
  const ehComissaoAgendada = (c) => (c.vendas?.tipo_venda === "consorcio" || c.vendas?.tipo_venda === "aluguel") && !c.vendas?.comissao_quitada;

  // Filtros da aba Comissões
  const comissoesFiltradas = useMemo(() => {
    let r = comissoes;
    if (isAdmin && fComCorretor !== "todos") r = r.filter(c => c.corretor_id === fComCorretor);
    if (fComTipo === "direta") r = r.filter(c => (c.nivel ?? 0) === 0 && !ehComissaoAgendada(c));
    else if (fComTipo === "residual") r = r.filter(c => (c.nivel ?? 0) > 0);
    else if (fComTipo === "consorcio") r = r.filter(ehComissaoAgendada);
    if (fComPeriodo !== "todos") {
      const agora = new Date();
      const inicio = fComPeriodo === "ano" ? new Date(agora.getFullYear(), 0, 1) : new Date(agora.getFullYear(), agora.getMonth(), 1);
      r = r.filter(c => new Date(c.criado_em) >= inicio);
    }
    if (fComBusca) {
      const q = fComBusca.toLowerCase();
      r = r.filter(c => (c.cliente_nome || c.vendas?.cliente_nome || "").toLowerCase().includes(q));
    }
    return r;
  }, [comissoes, isAdmin, fComCorretor, fComTipo, fComPeriodo, fComBusca]);

  const comissoesAgendadas = comissoesFiltradas.filter(ehComissaoAgendada);
  const comissoesNormais = comissoesFiltradas.filter(c => !ehComissaoAgendada(c));
  const totalAgendado = comissoesAgendadas.reduce((s, c) => s + parseFloat(c.comissao_valor ?? c.valor ?? 0), 0);

  // Volume vendas (todas comissões nivel=0 do corretor) últimos 90 dias
  const volume90Dias = comissoes
    .filter(c => (c.nivel ?? 0) === 0)
    .filter(c => new Date(c.criado_em) >= noventaDiasAtras)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);

  // Vendas ano corrente (nivel=0)
  const vendasAno = comissoes
    .filter(c => (c.nivel ?? 0) === 0)
    .filter(c => new Date(c.criado_em) >= inicioAno)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);

  const targets = [
    { id: "rede",      titulo: "Desempenho de rede",       alvoValor: 10000,   atual: comissaoRedeAno, bonus: 1000, label: "R$ 10k em comissão de rede (último ano)" },
    { id: "melhoria",  titulo: "Melhoria de desempenho",   alvoValor: 1000000, atual: volume90Dias,    bonus: 2000, label: "R$ 1M em volume de vendas (últimos 90 dias)" },
    { id: "anual",     titulo: "Desempenho anual",         alvoValor: 4000000, atual: vendasAno,       bonus: 3000, label: "R$ 4M em vendas no ano" },
  ];

  const totalCicloAberto = cicloAberto ? parseFloat(cicloAberto.total_ajustado ?? cicloAberto.total_bruto ?? 0) : 0;
  const totalPendenteNF = ciclosComStatus
    .filter(c => c._status === "nf_pendente")
    .reduce((s, c) => s + parseFloat(c.total_ajustado ?? c.total_bruto ?? 0), 0);
  const totalProntoPagar = ciclosComStatus
    .filter(c => ["nf_aprovada", "aguardando_pagamento"].includes(c._status))
    .reduce((s, c) => s + parseFloat(c.total_ajustado ?? c.total_bruto ?? 0), 0);
  const totalPago = ciclosComStatus
    .filter(c => c._status === "pago")
    .reduce((s, c) => s + parseFloat(c.total_ajustado ?? c.total_bruto ?? 0), 0);

  // ─── upload NF ─────────────────────────────────────────────────────
  function abrirModalNF(ciclo) {
    setUploadCiclo(ciclo);
    setNfNumero(ciclo.nf_numero || "");
    setNfFile(null);
    setUploadErr("");
  }
  function fecharModalNF() {
    setUploadCiclo(null); setNfNumero(""); setNfFile(null); setUploadErr("");
  }
  async function submitNF() {
    if (!uploadCiclo) return;
    if (!nfNumero.trim()) { setUploadErr("Número da NF é obrigatório."); return; }
    if (!nfFile)         { setUploadErr("Anexe o arquivo da NF."); return; }
    if (nfFile.size > 10 * 1024 * 1024) { setUploadErr("Máx 10 MB."); return; }
    if (!/pdf|image\//i.test(nfFile.type)) { setUploadErr("Use PDF ou imagem."); return; }

    setUploading(true); setUploadErr("");
    try {
      const ext = (nfFile.name.split(".").pop() || "bin").toLowerCase();
      const ownerUuid = uploadCiclo.corretor_id || meuUuid || "sem-uuid";
      const stamp = Date.now();
      const safeName = `nf-${nfNumero.replace(/[^a-z0-9-_]/gi, "_")}-${stamp}.${ext}`;
      const path = `corretores/${ownerUuid}/ciclo-${uploadCiclo.mes_ref}/${safeName}`;

      const { error: upErr } = await supabase.storage.from("nfs").upload(path, nfFile, {
        contentType: nfFile.type, upsert: false,
      });
      if (upErr) throw upErr;

      const { error: updErr } = await supabase.from("ciclos_pagamento").update({
        status: "nf_enviada",
        nf_numero: nfNumero,
        nf_url: path,
        nf_data_envio: new Date().toISOString(),
        nf_motivo_recusa: null,
        atualizado_em: new Date().toISOString(),
      }).eq("id", uploadCiclo.id);
      if (updErr) throw updErr;

      setCiclos(prev => prev.map(c => c.id === uploadCiclo.id
        ? { ...c, status: "nf_enviada", nf_numero: nfNumero, nf_url: path, nf_motivo_recusa: null }
        : c));
      fecharModalNF();
    } catch (e) {
      console.error("[Financeiro] upload NF:", e);
      setUploadErr(e.message || "Falha no envio da NF.");
    } finally { setUploading(false); }
  }

  async function abrirNF(path) {
    if (!path) return;
    try {
      const { data, error } = await supabase.storage.from("nfs").createSignedUrl(path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) { alert("Erro ao abrir NF: " + (e.message || e)); }
  }

  // ─── ações admin ───────────────────────────────────────────────────
  async function aprovarNF(ciclo) {
    if (!isSupabaseReady) return;
    const { error } = await supabase.from("ciclos_pagamento").update({
      status: "nf_aprovada",
      nf_data_aprovacao: new Date().toISOString(),
      atualizado_em: new Date().toISOString(),
    }).eq("id", ciclo.id);
    if (error) { alert("Erro: " + error.message); return; }
    setCiclos(prev => prev.map(c => c.id === ciclo.id
      ? { ...c, status: "nf_aprovada", nf_data_aprovacao: new Date().toISOString() }
      : c));
  }

  async function recusarNF(ciclo) {
    const motivo = prompt("Motivo da recusa:");
    if (!motivo) return;
    const { error } = await supabase.from("ciclos_pagamento").update({
      status: "nf_pendente", nf_motivo_recusa: motivo, nf_url: null,
      atualizado_em: new Date().toISOString(),
    }).eq("id", ciclo.id);
    if (error) { alert("Erro: " + error.message); return; }
    setCiclos(prev => prev.map(c => c.id === ciclo.id
      ? { ...c, status: "nf_pendente", nf_motivo_recusa: motivo, nf_url: null }
      : c));
  }

  async function marcarPago(ciclo) {
    if (!window.confirm(`Confirmar pagamento de ${fmt.brl(ciclo.total_ajustado ?? ciclo.total_bruto)} ao corretor?`)) return;
    const { error } = await supabase.from("ciclos_pagamento").update({
      status: "pago", data_pagamento_real: new Date().toISOString(),
      atualizado_em: new Date().toISOString(),
    }).eq("id", ciclo.id);
    if (error) { alert("Erro: " + error.message); return; }
    setCiclos(prev => prev.map(c => c.id === ciclo.id
      ? { ...c, status: "pago", data_pagamento_real: new Date().toISOString() }
      : c));
  }

  // Admin: força mudança de status livremente
  async function mudarStatus(ciclo, novoStatus) {
    if (novoStatus === ciclo.status) return;
    const { error } = await supabase.from("ciclos_pagamento").update({
      status: novoStatus,
      atualizado_em: new Date().toISOString(),
    }).eq("id", ciclo.id);
    if (error) { alert("Erro: " + error.message); return; }
    setCiclos(prev => prev.map(c => c.id === ciclo.id ? { ...c, status: novoStatus } : c));
  }

  // Admin: exclui ciclo
  async function excluirCiclo(ciclo) {
    const valor = fmt.brl(parseFloat(ciclo.total_ajustado ?? ciclo.total_bruto ?? 0));
    if (!window.confirm(`Excluir ciclo de ${fmtMes(ciclo.mes_ref)} (${valor})?\n\nAtenção: comissões individuais NÃO são apagadas. Próxima inserção de comissão recria o ciclo automaticamente.`)) return;
    const { error } = await supabase.from("ciclos_pagamento").delete().eq("id", ciclo.id);
    if (error) { alert("Erro: " + error.message); return; }
    setCiclos(prev => prev.filter(c => c.id !== ciclo.id));
  }

  // ─── render ────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Carregando financeiro...</div>;

  const TABS = [
    { id: "resumo",    label: "📊 Resumo" },
    { id: "ciclos",    label: "📄 NFs" },
    { id: "comissoes", label: isAdmin ? "💰 Comissões" : "💰 Vendas" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader icon="💰" title={isAdmin ? "Pagamentos" : "Financeiro"} subtitle={isAdmin ? "Aprovação de NFs e liberação de pagamentos" : "Suas comissões mês a mês"} />

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "8px 14px", borderRadius: T.r.md,
            background: tab === t.id ? T.brand + "22" : T.card,
            border: `1px solid ${tab === t.id ? T.brand : T.border}`,
            color: tab === t.id ? T.brand : T.textSec,
            cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ═══ RESUMO ═══ */}
      {tab === "resumo" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <KpiBox icon="🟢" label={`Mês aberto (${fmtMes(mesAtual)})`} valor={fmt.brl(totalCicloAberto)} cor={T.info}
              onClick={() => { setFCorretor("todos"); setFStatus("todos"); setFMes(mesAtual); setFBusca(""); setTab("ciclos"); }} />
            <KpiBox icon="📄" label="Pendente NF" valor={fmt.brl(totalPendenteNF)} cor={T.warn}
              onClick={() => { setFCorretor("todos"); setFStatus("nf_pendente"); setFMes("todos"); setFBusca(""); setTab("ciclos"); }} />
            <KpiBox icon="✅" label="Pronto p/ pagar" valor={fmt.brl(totalProntoPagar)} cor={T.ok}
              onClick={() => { setFCorretor("todos"); setFStatus("nf_aprovada"); setFMes("todos"); setFBusca(""); setTab("ciclos"); }} />
            <KpiBox icon="💰" label="Total pago" valor={fmt.brl(totalPago)} cor={T.gold}
              onClick={() => { setFCorretor("todos"); setFStatus("pago"); setFMes("todos"); setFBusca(""); setTab("ciclos"); }} />
          </div>
          {/* Próximo pagamento previsto — sempre acompanha o MÊS ATUAL (ciclo em aberto) */}
          <Card>
            <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Próximo pagamento previsto</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.gold }}>
                  {fmt.brl(totalCicloAberto)}
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                  Ciclo {fmtMes(mesAtual)} · {cicloAberto ? (STATUS_LABELS[cicloAberto._status]?.label || "Mês em aberto") : "Mês em aberto"}
                </div>
              </div>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                📅 {fmtData(cicloAberto?.data_pagamento_prevista || dataPrevistaPagamento(mesAtual))}
              </div>
            </div>
          </Card>
          {ciclos.length === 0 && (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>
              Nenhum ciclo ainda. Ciclos são criados automaticamente quando uma comissão é gerada.
            </div>
          )}

        </div>
      )}

      {/* ═══ CICLOS ═══ */}
      {tab === "ciclos" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Filtros */}
          <Card style={{ padding: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
              {isAdmin && (
                <Sel value={fCorretor} onChange={e => setFCorretor(e.target.value)}>
                  <option value="todos">👥 Todos corretores</option>
                  {corretores.filter(c => !c.banido && c.tipo !== "proprietario" && c.tipo !== "admin" && c.tipo !== "admin_master")
                    .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""))
                    .map(c => (
                      <option key={c._uuid || c.id} value={c._uuid}>{c.nome}</option>
                    ))}
                </Sel>
              )}
              <Sel value={fStatus} onChange={e => setFStatus(e.target.value)}>
                <option value="todos">📊 Todos status</option>
                <option value="aberto">🟢 Em aberto</option>
                <option value="nf_pendente">📄 NF pendente</option>
                <option value="nf_enviada">📤 NF enviada</option>
                <option value="nf_aprovada">✅ NF aprovada</option>
                <option value="aguardando_pagamento">⏳ Aguardando pgto</option>
                <option value="pago">💰 Pago</option>
              </Sel>
              <Sel value={fMes} onChange={e => setFMes(e.target.value)}>
                {mesesDisponiveis.map(m => (
                  <option key={m} value={m}>{m === "todos" ? "📅 Todos os meses" : `📅 ${fmtMes(m)}`}</option>
                ))}
              </Sel>
            </div>
            <div style={{ marginTop: 8 }}>
              <Input placeholder="🔍 Buscar por nome corretor ou nº NF..." value={fBusca} onChange={e => setFBusca(e.target.value)} />
            </div>
            {(fCorretor !== "todos" || fStatus !== "todos" || fMes !== "todos" || fBusca) && (
              <div style={{ marginTop: 8, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: 11, color: T.textDim }}>
                <span>{ciclosFiltrados.length} de {ciclosComStatus.length} ciclos</span>
                <button
                  onClick={() => { setFCorretor("todos"); setFStatus("todos"); setFMes("todos"); setFBusca(""); }}
                  style={{ background: "none", border: "none", color: T.brand, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", fontSize: 11 }}
                >🗑️ Limpar filtros</button>
              </div>
            )}
          </Card>

          {ciclosFiltrados.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>
              {ciclosComStatus.length === 0 ? "Nenhum ciclo ainda." : "Nenhum ciclo corresponde aos filtros."}
            </div>
          ) : ciclosFiltrados.map(ciclo => {
            const st = STATUS_LABELS[ciclo._status] || STATUS_LABELS.aberto;
            const corretor = corretorMap[ciclo.corretor_id];
            const valor = parseFloat(ciclo.total_ajustado ?? ciclo.total_bruto ?? 0);
            const ehMesAtual = ciclo.mes_ref === mesAtual;
            return (
              <Card key={ciclo.id} style={{ borderLeft: `3px solid ${st.cor}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, color: T.textDim }}>{st.icon} {fmtMes(ciclo.mes_ref)}{ehMesAtual && " · em andamento"}</div>
                    <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.gold, marginTop: 4 }}>
                      {fmt.brl(valor)}
                    </div>
                    <div style={{ padding: "2px 8px", borderRadius: 100, background: st.cor + "22", border: `1px solid ${st.cor}44`, fontSize: 10, color: st.cor, fontWeight: 600, display: "inline-block", marginTop: 6 }}>
                      {st.label}
                    </div>
                    {ciclo.nf_numero && (
                      ciclo.nf_url ? (
                        <button onClick={() => abrirNF(ciclo.nf_url)} style={{
                          marginLeft: 8, fontSize: 10, color: T.brand, fontWeight: 600,
                          background: "none", border: "none", padding: 0, cursor: "pointer",
                          textDecoration: "underline", fontFamily: "inherit",
                        }}>📎 Ver NF #{ciclo.nf_numero}</button>
                      ) : (
                        <span style={{ marginLeft: 8, fontSize: 10, color: T.textSub }}>NF #{ciclo.nf_numero}</span>
                      )
                    )}
                    {ciclo.nf_motivo_recusa && (
                      <div style={{ fontSize: 10, color: T.err, marginTop: 4 }}>Recusa: {ciclo.nf_motivo_recusa}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {isAdmin && corretor && <div style={{ fontSize: 12, color: T.text }}>{corretor.nome}</div>}
                    <div style={{ fontSize: 10, color: T.textSub }}>
                      Pagamento previsto:<br/>📅 {fmtData(ciclo.data_pagamento_prevista)}
                    </div>
                    {ciclo.data_pagamento_real && (
                      <div style={{ fontSize: 10, color: T.ok, marginTop: 2 }}>
                        Pago em {fmtData(ciclo.data_pagamento_real)}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {!isAdmin && ciclo._status === "nf_pendente" && (
                    <Btn variant="ghost" onClick={() => abrirModalNF(ciclo)} style={{ fontSize: 11 }}>📤 Enviar NF</Btn>
                  )}
                  {!isAdmin && ciclo._status === "nf_enviada" && (
                    <Btn variant="ghost" onClick={() => abrirModalNF(ciclo)} style={{ fontSize: 11 }}>🔄 Substituir NF</Btn>
                  )}
                  {isAdmin && ciclo._status === "nf_enviada" && (
                    <>
                      <Btn variant="ghost" onClick={() => aprovarNF(ciclo)} style={{ fontSize: 11, color: T.ok }}>✅ Aprovar NF</Btn>
                      <Btn variant="ghost" onClick={() => recusarNF(ciclo)} style={{ fontSize: 11, color: T.err }}>❌ Recusar NF</Btn>
                    </>
                  )}
                  {isAdmin && ["nf_aprovada", "aguardando_pagamento"].includes(ciclo._status) && (
                    <Btn variant="ghost" onClick={() => marcarPago(ciclo)} style={{ fontSize: 11, color: T.ok }}>💳 Marcar como Pago</Btn>
                  )}
                </div>

                {/* Admin: força status + excluir */}
                {isAdmin && (
                  <div style={{
                    marginTop: 12, paddingTop: 10,
                    borderTop: `1px dashed ${T.border}`,
                    display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                  }}>
                    <span style={{ fontSize: 10, color: T.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Admin:</span>
                    <select
                      value={ciclo.status || "aberto"}
                      onChange={e => mudarStatus(ciclo, e.target.value)}
                      style={{
                        padding: "5px 8px", fontSize: 11, fontFamily: "inherit",
                        background: T.card, color: T.text,
                        border: `1px solid ${T.borderMid}`, borderRadius: T.r.md,
                        cursor: "pointer",
                      }}
                    >
                      <option value="aberto">aberto</option>
                      <option value="fechado">fechado</option>
                      <option value="nf_pendente">nf_pendente</option>
                      <option value="nf_enviada">nf_enviada</option>
                      <option value="nf_aprovada">nf_aprovada</option>
                      <option value="aguardando_pagamento">aguardando_pagamento</option>
                      <option value="pago">pago</option>
                    </select>
                    <button
                      onClick={() => excluirCiclo(ciclo)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: T.err, fontSize: 11, fontFamily: "inherit",
                        textDecoration: "underline",
                      }}
                    >🗑️ Excluir ciclo</button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ DETALHE (comissões individuais) ═══ */}
      {tab === "comissoes" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Filtros */}
          {comissoes.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              {isAdmin && (
                <Sel value={fComCorretor} onChange={e => setFComCorretor(e.target.value)}>
                  <option value="todos">👤 Todos corretores</option>
                  {(corretores || []).map(c => <option key={c._uuid || c.id} value={c._uuid}>{c.nome}</option>)}
                </Sel>
              )}
              <Sel value={fComTipo} onChange={e => setFComTipo(e.target.value)}>
                <option value="todos">📊 Todos os tipos</option>
                <option value="direta">💼 Venda direta</option>
                <option value="residual">🔗 Residual (rede)</option>
                <option value="consorcio">📅 Agendada (consórcio/aluguel)</option>
              </Sel>
              <Sel value={fComPeriodo} onChange={e => setFComPeriodo(e.target.value)}>
                <option value="todos">📅 Todo o período</option>
                <option value="mes">Este mês</option>
                <option value="ano">Este ano</option>
              </Sel>
              <div style={{ flex: 1, minWidth: 180 }}>
                <Input placeholder="Buscar por cliente..." value={fComBusca} onChange={e => setFComBusca(e.target.value)} />
              </div>
              {isAdmin && (
                <Btn variant="ghost" onClick={recalcularTodasAward} title="Corrige valores antigos pela premiação atual — temporário">
                  🔄 Recalcular comissões pela premiação
                </Btn>
              )}
            </div>
          )}

          {comissoesFiltradas.length === 0 && (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>
              {comissoes.length === 0 ? "Nenhuma comissão ainda." : "Nenhuma comissão com esses filtros."}
            </div>
          )}

          {/* Comissões agendadas (consórcio não quitado) — fora do "a pagar" */}
          {comissoesAgendadas.length > 0 && (
            <div style={{
              background: T.card, border: `1px solid ${T.brand}55`,
              borderRadius: T.r.md, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ fontSize: 11, color: T.brand, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>
                📅 Comissões agendadas · Consórcio / Aluguel
              </div>
              <div style={{ fontSize: 10, color: T.textDim }}>
                {isAdmin
                  ? `Total ${fmt.brl(totalAgendado)} — fora do "a pagar agora". Liberam na quitação das parcelas.`
                  : `Total ${fmt.brl(totalAgendado)} a receber nas datas previstas (após a quitação do consórcio).`}
              </div>
              {comissoesAgendadas.map(c => {
                const valorA = parseFloat(c.comissao_valor ?? c.valor ?? 0);
                const receb = corretorMap[c.corretor_id];
                const dataPrev = c.vendas?.comissao_data_prevista;
                return (
                  <div key={c.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap",
                    padding: "8px 10px", background: T.cardSoft, border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${T.brand}`, borderRadius: T.r.md,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
                        {c.cliente_nome || c.vendas?.cliente_nome || "—"}
                        {isAdmin && receb && <span style={{ color: T.textSub, fontWeight: 500 }}> · 👤 {receb.nome}</span>}
                      </div>
                      <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>
                        {dataPrev
                          ? `📅 ${isAdmin ? "prev. pagamento" : "a receber em"} ${new Date(dataPrev + "T00:00:00").toLocaleDateString("pt-BR")}`
                          : "📅 data prevista não definida"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className="serif" style={{ fontSize: 15, fontWeight: 700, color: T.gold }}>{fmt.brl(valorA)}</div>
                      {isAdmin && (
                        <span style={{ fontSize: 9, color: T.textDim, fontStyle: "italic" }}>quitar na venda ↗</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {comissoesNormais.map(c => {
            const valor = parseFloat(c.comissao_valor ?? c.valor ?? 0);
            const recebedor = corretorMap[c.corretor_id];
            const isDireta = (c.nivel ?? 0) === 0;
            const vendedorIdDaVenda = c.vendas?.corretor_id;
            const vendedor = vendedorIdDaVenda ? corretorMap[vendedorIdDaVenda] : null;
            const corBarra = isDireta ? T.brand : T.gold;
            const chipBg = isDireta ? T.brand + "22" : T.gold + "22";
            const chipCor = isDireta ? T.brand : T.gold;
            const chipLabel = isDireta ? "💼 Venda direta" : `🔗 Residual N${c.nivel}`;
            const pct = parseFloat(c.percentual ?? c.comissao_pct ?? 0);
            const valorVenda  = parseFloat(c.vendas?.valor ?? c.valor_venda ?? 0);
            const freteValor  = parseFloat(c.vendas?.frete_valor ?? c.vendas?.frete ?? 0);
            const taxaCartao  = parseFloat(c.vendas?.taxa_cartao ?? 0);
            const desconto    = parseFloat(c.vendas?.desconto_concedido ?? 0);
            const liquido     = parseFloat(c.vendas?.valor_liquido ?? c.valor_comissionavel ?? (valorVenda - freteValor - taxaCartao - desconto));
            // Pra residual: comissão direta do vendedor (base do residual) = valor / (pct/100)
            const comissaoDiretaBase = !isDireta && pct > 0 ? valor / (pct / 100) : 0;

            return (
              <div key={c.id} style={{
                display: "flex", flexDirection: "column", gap: 8,
                padding: "12px 14px", background: T.card, border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${corBarra}`, borderRadius: T.r.md,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 100,
                        background: chipBg, border: `1px solid ${chipCor}44`,
                        fontSize: 9, color: chipCor, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase",
                      }}>{chipLabel}</span>
                      <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{c.cliente_nome || c.vendas?.cliente_nome || "—"}</div>
                    </div>
                    <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>
                      Ciclo {c.mes_ref ? fmtMes(c.mes_ref) : "—"} · {pct}%
                      {!isDireta && vendedor && ` · venda feita por ${vendedor.nome}`}
                      {isAdmin && recebedor && ` · 👤 ${recebedor.nome}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="serif" style={{ fontSize: 16, fontWeight: 700, color: T.gold }}>{fmt.brl(valor)}</div>
                    {isAdmin && isDireta && (
                      <button
                        onClick={() => recalcularComissaoAward(c)}
                        title="Recalcular pela premiação (award) atual do corretor"
                        style={{
                          background: T.brand + "22", border: `1px solid ${T.brand}44`,
                          color: T.brand, fontSize: 10, padding: "2px 8px",
                          borderRadius: 4, cursor: "pointer", fontFamily: "inherit",
                        }}
                      >🎯 Award</button>
                    )}
                    {isMaster && (
                      <button
                        onClick={() => editarValorComissao(c)}
                        title="Editar valor manualmente (Master)"
                        style={{
                          background: T.gold + "22", border: `1px solid ${T.gold}44`,
                          color: T.gold, fontSize: 10, padding: "2px 8px",
                          borderRadius: 4, cursor: "pointer", fontFamily: "inherit",
                        }}
                      >✏️ Editar</button>
                    )}
                  </div>
                </div>

                {/* Quebra detalhada por tipo */}
                {isDireta ? (
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8,
                    paddingTop: 8, borderTop: `1px dashed ${T.border}`, fontSize: 10,
                  }}>
                    <MiniBreak label="Valor venda" valor={fmt.brl(valorVenda)} />
                    <MiniBreak label="Frete" valor={fmt.brl(freteValor)} cor={T.warn} />
                    {(() => {
                      const comps = Array.isArray(c.vendas?.taxa_cartao_comprovantes)
                        ? c.vendas.taxa_cartao_comprovantes
                        : (c.vendas?.taxa_cartao_comprovante ? [c.vendas.taxa_cartao_comprovante] : []);
                      return (
                        <MiniBreak
                          label="Taxa cartão"
                          valor={
                            comps.length > 0 ? (
                              <span>
                                {fmt.brl(taxaCartao)}
                                {comps.map((url, i) => (
                                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color: T.brand, marginLeft: 4, textDecoration: "underline", fontSize: 9 }}>📎{comps.length > 1 ? i + 1 : ""}</a>
                                ))}
                              </span>
                            ) : fmt.brl(taxaCartao)
                          }
                          cor={T.warn}
                        />
                      );
                    })()}
                    {desconto > 0 && <MiniBreak label="Desconto" valor={fmt.brl(desconto)} cor={T.warn} />}
                    <MiniBreak label="Líquido" valor={fmt.brl(liquido)} cor={T.ok} />
                    <MiniBreak label={`Comissão ${pct}%`} valor={fmt.brl(valor)} cor={T.gold} destaque />
                  </div>
                ) : (
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8,
                    paddingTop: 8, borderTop: `1px dashed ${T.border}`, fontSize: 10,
                  }}>
                    <MiniBreak label="Vendedor(a)" valor={vendedor?.nome || "—"} cor={T.brand} />
                    <MiniBreak label="Comissão direta dele(a)" valor={fmt.brl(comissaoDiretaBase)} cor={T.textSub} />
                    <MiniBreak label={`Seu residual ${pct}%`} valor={fmt.brl(valor)} cor={T.gold} destaque />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MODAL UPLOAD NF ═══ */}
      {uploadCiclo && (
        <Modal title={`Enviar NF do ciclo ${fmtMes(uploadCiclo.mes_ref)}`} width={520} onClose={() => !uploading && fecharModalNF()}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: T.textSub }}>
              Valor total: <strong style={{ color: T.gold }}>
                {fmt.brl(parseFloat(uploadCiclo.total_ajustado ?? uploadCiclo.total_bruto ?? 0))}
              </strong>
              <br/>Pagamento previsto: 📅 {fmtData(uploadCiclo.data_pagamento_prevista)}
            </div>

            <div>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Número da NF</div>
              <Input value={nfNumero} onChange={e => setNfNumero(e.target.value)} placeholder="ex: 000123" disabled={uploading} />
            </div>

            <div>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Arquivo (PDF, JPG ou PNG · máx 10 MB)</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/*"
                onChange={e => setNfFile(e.target.files?.[0] || null)}
                disabled={uploading}
                style={{
                  display: "block", width: "100%", padding: 10, fontSize: 12,
                  background: T.surface, color: T.text,
                  border: `1px dashed ${T.borderMid}`, borderRadius: T.r.md,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              />
              {nfFile && (
                <div style={{ marginTop: 6, fontSize: 11, color: T.textSub }}>
                  📎 {nfFile.name} · {(nfFile.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>

            {uploadErr && (
              <div style={{ padding: "8px 12px", borderRadius: T.r.md, background: T.err + "15", border: `1px solid ${T.err}44`, color: T.err, fontSize: 12 }}>
                ⚠️ {uploadErr}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <Btn variant="ghost" onClick={fecharModalNF} disabled={uploading}>Cancelar</Btn>
              <Btn onClick={submitNF} disabled={uploading}>{uploading ? "Enviando…" : "📤 Enviar NF"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MiniBreak({ label, valor, cor, destaque }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 12, color: cor || T.text, fontWeight: destaque ? 700 : 500, marginTop: 2 }}>{valor}</div>
    </div>
  );
}

function KpiBox({ icon, label, valor, cor, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${cor}`,
        borderRadius: T.r.md, padding: "12px 10px",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color .15s, transform .05s",
      }}
      onMouseEnter={onClick ? (e => { e.currentTarget.style.borderColor = cor; }) : undefined}
      onMouseLeave={onClick ? (e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = cor; }) : undefined}
    >
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{icon} {label}{onClick && <span style={{ color: cor, marginLeft: 4 }}>›</span>}</div>
      <div className="serif" style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{valor}</div>
    </div>
  );
}
